import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Typography, useTheme } from '@mui/material';
import { getReportSurfaceSx } from './report-design';
import { formatReportDateRangeLabel } from './reportStatusCards';

// Report dates are shared through the existing dc event, including other date controls.
export function useReportRange() {
    const read = () => ({ sDate: sessionStorage.getItem('sDate') || '', eDate: sessionStorage.getItem('eDate') || '' });
    const [range, setRange] = useState(read);
    useEffect(() => {
        const update = () => setRange(read());
        window.addEventListener('dc', update);
        return () => window.removeEventListener('dc', update);
    }, []);
    return range;
}

export function useReportLoading(loading, setParentLoading) {
    useEffect(() => {
        setParentLoading?.(Boolean(loading));
        return () => setParentLoading?.(false);
    }, [loading, setParentLoading]);
}

export default function ReportLead({ eyebrow, title, description, badge, actions, heading = 'h1', showDates = true }) {
    const range = useReportRange();
    const dateLabel = formatReportDateRangeLabel(range.sDate, range.eDate);
    return <Box data-report-lead sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
            {(eyebrow || (showDates && dateLabel)) && <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 16px', mb: 0.5 }}>
                {eyebrow && <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', lineHeight: 1.5 }}>{eyebrow}</Typography>}
                {showDates && dateLabel && <Typography variant="caption" color="text.secondary" aria-label="Selected report dates">{dateLabel}</Typography>}
            </Box>}
            <Typography component={heading} sx={{ fontSize: { xs: '1.4rem', md: '1.75rem' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.2, overflowWrap: 'anywhere' }}>{title}</Typography>
            {description && <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 900, fontSize: '0.875rem', lineHeight: 1.5 }}>{description}</Typography>}
        </Box>
        {(badge || actions) && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
            {badge && <Chip size="small" label={badge} sx={{ maxWidth: '100%', bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', '& .MuiChip-label': { whiteSpace: 'normal', height: 'auto' }, height: 'auto', minHeight: 24 }} />}
            {actions}
        </Box>}
    </Box>;
}

export function ReportLeadCard(props) {
    const theme = useTheme();
    return <Card component="section" elevation={0} sx={{ ...getReportSurfaceSx(theme), position: 'relative', mb: 2 }}><CardContent sx={{ p: { xs: 2, md: 2.5 } }}><ReportLead {...props} /></CardContent></Card>;
}
